/**
 * Frontend model representing a job in the schedule-apply flow.
 *
 * Matches Android: com.indeed.android.ghostwriter.models.ScheduleApplyJob
 */

import type { ScheduleApplyFormFieldGroup } from './ScheduleApplyFormField';

/**
 * One persisted instance of a {@link ScheduleApplyFormFieldGroup}'s
 * response set, sourced from `GhostwriterApplication.currentResponseSet
 * .responseGroups[]`. Each form field group has zero or more instance
 * seeds: non-repeatable groups have at most one, repeatable groups may
 * have many (`canCreateNewResponseGroup === true`).
 *
 * `displayOrder` is the server-provided ordering hint; instance seeds are
 * stored sorted ascending by `displayOrder` (nullish last) so callers can
 * render them in stable order.
 *
 * `fieldValues` is keyed by the bare `formField.id` and carries the
 * per-instance value — string for unbound fields, string[] for options
 * fields. The empty value (`''` / `[]`) means "answered with empty",
 * which is distinct from "no answer present" (in which case the
 * formFieldId is simply absent from the map).
 */
export interface ScheduleApplyResponseGroupInstanceSeed {
    /** Stable client-side instance ID minted at hydration time. */
    instanceId: string;
    /** Server-provided display order, or `null` when missing. */
    displayOrder: number | null;
    /** Optional human-readable name (e.g. "Education History"). */
    name: string | null;
    /** Per-formFieldId values for this instance. */
    fieldValues: ReadonlyMap<string, string | string[]>;
}

export enum ScheduleApplyStatusSeverity {
    Default = 'default',
    NeedsInformation = 'needsInformation',
    Ready = 'ready',
    Error = 'error',
}

export interface ScheduleApplyStatus {
    displayValue: string;
    severity: ScheduleApplyStatusSeverity;
}

export const ScheduleApplyStatuses = {
    AnalyzingJob: {
        displayValue: 'Analyzing Job',
        severity: ScheduleApplyStatusSeverity.Default,
    },
    ReadyToReview: {
        displayValue: 'Ready to review',
        severity: ScheduleApplyStatusSeverity.Ready,
    },
    MissingRequiredInfo: {
        displayValue: 'Missing required info',
        severity: ScheduleApplyStatusSeverity.NeedsInformation,
    },
    AutoApplyFailed: {
        displayValue: 'Auto apply failed',
        severity: ScheduleApplyStatusSeverity.Error,
    },
    SubmissionFailed: {
        displayValue: 'Submission failed',
        severity: ScheduleApplyStatusSeverity.Error,
    },
    FillingOutApplication: {
        displayValue: 'Filling out application',
        severity: ScheduleApplyStatusSeverity.Default,
    },
    SubmittingApplication: {
        displayValue: 'Submitting application',
        severity: ScheduleApplyStatusSeverity.Default,
    },
    RetryPendingCaptcha: {
        displayValue: 'Submitting application',
        severity: ScheduleApplyStatusSeverity.Default,
    },
    Applied: {
        displayValue: 'Applied',
        severity: ScheduleApplyStatusSeverity.Ready,
    },
    NotStarted: {
        displayValue: 'Not Started',
        severity: ScheduleApplyStatusSeverity.Default,
    },
    AutoApplyCancelled: {
        displayValue: 'Auto apply canceled',
        severity: ScheduleApplyStatusSeverity.Default,
    },
} as const satisfies Record<string, ScheduleApplyStatus>;

export interface ScheduleApplyJob {
    jobKey: string;
    jobUrl: string;
    title: string;
    descriptionHtml: string;
    location: string;
    company: string;
    salary: string | null;
    applicationId: string;
    applicationUpdatedDate: string;
    applicationCreatedDate: string;
    applicationNotes: string | null;
    applicationStatus: ScheduleApplyStatus;
    externalApplicationId: string | null;
    applicationFormFieldGroups: ScheduleApplyFormFieldGroup[];
    /**
     * Per-form-field-group ordered list of persisted response group
     * instances, used to seed the draft state with multi-instance content
     * (e.g. two Education History entries). For non-repeatable groups,
     * this list has at most one element. Keyed by `formFieldGroup.id`.
     * Groups with no matching response group are absent from the map.
     */
    responseGroupSeeds: ReadonlyMap<string, ReadonlyArray<ScheduleApplyResponseGroupInstanceSeed>>;
}
